// EmpNav.jsx — Fixed: instant dropdown, sliding bg, scroll expand, shrink animation
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./EmpNav.css";
import Logo from "../../../../assets/logo.png";
import Login from "../../Modal/Login/Login";
import Calculator from "../../../../assets/gif/calculator.gif";
import ATS from "../../../../assets/gif/ats-checker.gif";
import ATSStatic from "../../../../assets/gif/ats-static.webp";
import Resume from "../../../../assets/gif/resume-builder.gif";
import ResumeStatic from "../../../../assets/gif/resume-static.png";
import CarrerTalk from "../../../../assets/gif/career-talk.gif";
import CarrerTalkStatic from "../../../../assets/gif/career-talk-static.png";
import CalculatorStatic from "../../../../assets/gif/calculator-static.webp";

// ── Calculator tool GIF / static imports ──────────────────────
// Add your actual GIF and static image paths here.
// Pattern: each tool needs a `gif` (animated) and `png` (static/thumbnail).
// If you don't have a dedicated GIF for a tool yet, reuse CalculatorStatic
// for both so the component still renders correctly.
import HRAGif          from "../../../../assets/gif/HRAGif.gif";
import HRAStatic       from "../../../../assets/gif/HRA-static.webp";
import SalaryGif       from "../../../../assets/gif/salary-calculator.gif";
import SalaryStatic    from "../../../../assets/gif/salary-static.png";
import PFGif           from "../../../../assets/gif/pf-calculator.gif";
import PFStatic        from "../../../../assets/gif/pf-static.png";
import NPSGif          from "../../../../assets/gif/pf-calculator.gif";
import NPSStatic       from "../../../../assets/gif/pf-calculator.gif";
import PPFGif          from "../../../../assets/gif/pf-calculator.gif";
import PPFStatic       from "../../../../assets/gif/pf-calculator.gif";
import TaxGif          from "../../../../assets/gif/pf-calculator.gif";
import TaxStatic       from "../../../../assets/gif/pf-calculator.gif";
import RetirementGif   from "../../../../assets/gif/pf-calculator.gif";
import RetirementStatic from "../../../../assets/gif/pf-calculator.gif";
import SalarySlipGif   from "../../../../assets/gif/pf-calculator.gif";
import SalarySlipStatic from "../../../../assets/gif/pf-calculator.gif";

const TABS = [
  { id: "careertalk", label: "Career Talk", gif: CarrerTalk, png: CarrerTalkStatic, path: "/employee/hrtalk",             field: "role"       },
  { id: "resume",     label: "Resume",      gif: Resume,     png: ResumeStatic,     path: "/employee/resume-builder",     field: "role"       },
  { id: "ats",        label: "ATS Check",   gif: ATS,        png: ATSStatic,        path: "/employee/ats-score-checker",  field: "role"       },
  { id: "Calculator", label: "Calculator",  gif: Calculator, png: CalculatorStatic, field: "calculator" },
];

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const TOOLS = [
  { id: "resume-builder", label: "Resume Builder",    icon: "✅", path: "/employee/resume-builder",    desc: "Build your customised resume." },
  { id: "ats",            label: "ATS Score Checker", icon: "📄", path: "/employee/ats-score-checker", desc: "Check how well your resume performs in ATS." },
  { id: "salaryguide",    label: "Salary Guide",      icon: "📋", path: "/employee/salary-guide",      desc: "Explore market salaries for any role." },
];

// ── Each tool now has gif + png for hover animation ─────────────
const CALCULATOR_TOOLS = [
  { id: "hra",        label: "HRA Calculator",        icon: "🏠", gif: HRAGif,          png: HRAStatic,         path: "/employee/hra-calculator",        desc: "Check your HRA exemption instantly." },
  { id: "salary",     label: "Salary Calculator",     icon: "💰", gif: SalaryGif,       png: SalaryStatic,      path: "/employee/salary-calculator",     desc: "Estimate take-home salary." },
  { id: "pf",         label: "PF Calculator",         icon: "📊", gif: PFGif,           png: PFStatic,          path: "/employee/pf-calculator",         desc: "Plan Provident Fund growth." },
  { id: "nps",        label: "NPS Calculator",        icon: "📈", gif: NPSGif,          png: NPSStatic,         path: "/employee/nps-calculator",        desc: "Project your pension corpus." },
  { id: "ppf",        label: "PPF Calculator",        icon: "🏦", gif: PPFGif,          png: PPFStatic,         path: "/employee/ppf-calculator",        desc: "Estimate PPF maturity." },
  { id: "tax",        label: "Tax Calculator",        icon: "🏛️", gif: TaxGif,          png: TaxStatic,         path: "/employee/tax-calculator",        desc: "Compare tax regimes." },
  { id: "retirement", label: "Retirement Calculator", icon: "🎯", gif: RetirementGif,   png: RetirementStatic,  path: "/employee/retirement-calculator", desc: "Plan retirement savings." },
  { id: "salaryslip", label: "Salary Slip",           icon: "📄", gif: SalarySlipGif,   png: SalarySlipStatic,  path: "/employee/salaryslip",            desc: "Generate salary slip." },
];

const POPULAR_ROLES = [
  { icon: "💻", label: "Software Engineer",     sub: "Most searched today" },
  { icon: "📊", label: "Data Scientist",        sub: "High demand" },
  { icon: "🎨", label: "UI/UX Designer",        sub: "Creative roles" },
  { icon: "📱", label: "Product Manager",       sub: "Strategy & growth" },
  { icon: "☁️", label: "DevOps Engineer",       sub: "Infrastructure" },
  { icon: "🤖", label: "ML Engineer",           sub: "AI & Machine Learning" },
  { icon: "📣", label: "Digital Marketing",     sub: "Growth & SEO" },
  { icon: "🔐", label: "Cybersecurity Analyst", sub: "High paying" },
];

const POPULAR_CITIES = [
  { icon: "🏙️", label: "Bangalore",   sub: "Silicon Valley of India" },
  { icon: "🌆", label: "Mumbai",      sub: "Financial capital" },
  { icon: "🏛️", label: "Delhi / NCR", sub: "Gurgaon · Noida included" },
  { icon: "💻", label: "Hyderabad",   sub: "Tech hub" },
  { icon: "🎓", label: "Pune",        sub: "IT & engineering" },
  { icon: "🌊", label: "Chennai",     sub: "Automotive & IT" },
  { icon: "🌐", label: "Remote",      sub: "Work from anywhere" },
  { icon: "🏰", label: "Jaipur",      sub: "Emerging tech scene" },
];

const SALARY_OPTIONS = [
  { icon: "🌱", label: "0 – 3 LPA",   sub: "Entry level / Fresher", val: "0–3 LPA"   },
  { icon: "🚀", label: "3 – 6 LPA",   sub: "Junior professional",   val: "3–6 LPA"   },
  { icon: "⚡", label: "6 – 10 LPA",  sub: "Mid-level",             val: "6–10 LPA"  },
  { icon: "💎", label: "10 – 20 LPA", sub: "Senior",                val: "10–20 LPA" },
  { icon: "🏆", label: "20+ LPA",     sub: "Lead / Expert",         val: "20+ LPA"   },
];

const EXPERIENCE_OPTIONS = [
  { icon: "🌱", label: "Fresher",       sub: "0 years experience", val: "Fresher"  },
  { icon: "🚀", label: "1 – 3 Years",  sub: "Junior level",       val: "1–3 Yrs"  },
  { icon: "⚡", label: "3 – 5 Years",  sub: "Mid-level",          val: "3–5 Yrs"  },
  { icon: "💎", label: "5 – 10 Years", sub: "Senior level",       val: "5–10 Yrs" },
  { icon: "🏆", label: "10+ Years",    sub: "Expert / Director",  val: "10+ Yrs"  },
];

/* ── Auth helpers ── */
function readStoredUser() {
  try {
    const raw = localStorage.getItem("th_user") || localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && (parsed.role || parsed.email) ? parsed : null;
  } catch { return null; }
}
function readStoredToken() {
  return localStorage.getItem("th_token") || localStorage.getItem("token") || "";
}
function saveUser(user, token) {
  try {
    localStorage.setItem("th_user", JSON.stringify(user));
    localStorage.setItem("th_token", token);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("token", token);
  } catch {}
}
function clearUser() {
  ["th_user","th_token","user","token"].forEach(k => { try { localStorage.removeItem(k); } catch {} });
}

/* ══════════════════════════════════════════════════════════
   CALC DROPDOWN  — GIF / static swap on item hover
══════════════════════════════════════════════════════════ */
function CalcDropdown({ anchorRef, dropRef, open, onClose, onMouseEnter, onMouseLeave }) {
  const navigate = useNavigate();
  const [style, setStyle] = useState({});

  // Track which item is hovered so we can show its GIF
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const reposition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const panelWidth = 640;
      let left = rect.left + rect.width / 2 - panelWidth / 2;
      left = Math.max(12, Math.min(left, window.innerWidth - panelWidth - 12));
      setStyle({ top: rect.bottom + 10, left, width: panelWidth });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, anchorRef]);

  // Reset hovered item when dropdown closes
  useEffect(() => {
    if (!open) setHoveredId(null);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      ref={dropRef}
      style={{
        position: "fixed",
        ...style,
        zIndex: 99998,
        background: "#fff",
        borderRadius: 20,
        boxShadow: "0 8px 40px rgba(0,0,0,.13), 0 1.5px 6px rgba(0,0,0,.07)",
        padding: "14px 12px",
        animation: "nsbDdIn .18s cubic-bezier(.4,0,.2,1)",
        overflow: "hidden",
      }}
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div style={{
        fontSize: 10.5, fontWeight: 700, color: "#9ca3af",
        letterSpacing: ".08em", textTransform: "uppercase",
        padding: "2px 8px 10px",
      }}>
        Calculators
      </div>

      {/* 4-column grid */}
      <div
        className="calc-dropdown-grid"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4 }}
      >
        {CALCULATOR_TOOLS.map((item) => {
          const isActive  = window.location.pathname === item.path;
          const isHovered = hoveredId === item.id;
          // Show animated GIF on hover, static image otherwise
          const imgSrc    = isHovered ? item.gif : item.png;

          return (
            <button
              key={item.id}
              role="menuitem"
              onClick={() => { onClose(); navigate(item.path); }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 7,
                padding: "12px 8px",
                borderRadius: 12,
                border: isActive
                  ? "1.5px solid #d1d5db"
                  : isHovered
                    ? "1.5px solid #e5e7eb"
                    : "1.5px solid transparent",
                background: isActive
                  ? "#f0f2eb"
                  : isHovered
                    ? "#f3f4f6"
                    : "transparent",
                cursor: "pointer",
                textAlign: "center",
                transition: "background .13s, border-color .13s",
                width: "100%",
              }}
            >
              {/* GIF / static image icon */}
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 11,
                overflow: "hidden",
                background: isActive ? "#e8ebe0" : isHovered ? "#f0f2eb" : "#f9fafb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                border: isHovered || isActive ? "1.5px solid #e2e5d8" : "1.5px solid transparent",
                transition: "background .15s, border-color .15s",
              }}>
                {/* Use img if gif/png are provided, fallback to emoji icon */}
                {item.gif && item.png ? (
                  <img
                    src={imgSrc}
                    alt={item.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      // Prevent layout shift while GIF loads
                      imageRendering: "auto",
                    }}
                    // Graceful fallback: if GIF fails, show emoji
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                {/* Emoji fallback (hidden when image loads, shown if it errors) */}
                <span style={{
                  fontSize: 22,
                  lineHeight: 1,
                  display: item.gif && item.png ? "none" : "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}>
                  {item.icon}
                </span>
              </div>

              {/* Label + description */}
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{
                  fontSize: 11.5,
                  fontWeight: isActive || isHovered ? 700 : 600,
                  color: isActive ? "#263012" : isHovered ? "#111827" : "#374151",
                  lineHeight: 1.2,
                }}>
                  {item.label}
                </span>
                <span style={{
                  fontSize: 10,
                  color: isHovered ? "#6b7280" : "#9ca3af",
                  lineHeight: 1.3,
                  transition: "color .13s",
                }}>
                  {item.desc}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
}

function getProfilePath(user) {
  if (!user) return "/employee/profile";
  const role = (user.role || "").toLowerCase().trim();
  if (role === "admin")    return "/admin";
  if (role === "employer") return "/employer/recruiter-dashboard";
  return "/employee/profile";
}
function getDisplayName(user) {
  if (!user) return "U";
  const first = user.firstName || user.first_name || "";
  const last  = user.lastName  || user.last_name  || "";
  const full  = `${first} ${last}`.trim();
  return full || user.username || user.name || user.email || "U";
}
function getProfilePhoto(user) {
  if (!user) return null;
  const raw = user.profile_photo || user.profilePhoto || user.photo || user.avatar || user.avatar_url || user.picture || user.profile_pic || null;
  if (!raw) return null;
  if (raw.startsWith("http")) return raw;
  const origin = (API_BASE || "").replace(/\/api\/?$/, "");
  return `${origin}${raw}`;
}
const AVATAR_COLORS = ["#263012","#0891b2","#394420","#263012","#263012","#7c3aed","#0284c7","#db2777"];
function avatarColor(name) {
  const c = (name || "U")[0].toUpperCase().charCodeAt(0);
  return AVATAR_COLORS[c % AVATAR_COLORS.length];
}

/* ══════════════════════════════════════════════════════════
   FULL SEARCH BAR CORE
══════════════════════════════════════════════════════════ */
function SearchBarCore({ externalActiveField, onExternalFieldHandled, onCollapse, autoFocusField }) {
  const [keyword,     setKeyword]     = useState("");
  const [location,    setLocation]    = useState("");
  const [salary,      setSalary]      = useState("");
  const [experience,  setExperience]  = useState("");
  const [activeField, setActiveField] = useState(null);
  const [roleQuery,   setRoleQuery]   = useState("");
  const [sliderStyle, setSliderStyle] = useState({ opacity: 0 });

  const barRef       = useRef(null);
  const fieldRefs    = useRef({});
  const skipCloseRef = useRef(false);
  const navigate     = useNavigate();

  const FIELD_ORDER = ["role", "loc", "salary", "exp"];

  useEffect(() => {
    if (!activeField || !barRef.current) { setSliderStyle({ opacity: 0 }); return; }
    const fieldEl  = fieldRefs.current[activeField];
    if (!fieldEl) return;
    const barRect   = barRef.current.getBoundingClientRect();
    const fieldRect = fieldEl.getBoundingClientRect();
    setSliderStyle({
      opacity: 1,
      left:   fieldRect.left   - barRect.left,
      width:  fieldRect.width,
      height: fieldRect.height,
      top:    fieldRect.top    - barRect.top,
    });
  }, [activeField]);

  useEffect(() => {
    const valid = ["role", "loc", "salary", "exp"];
    if (externalActiveField && valid.includes(externalActiveField)) {
      setActiveField(externalActiveField);
      onExternalFieldHandled && onExternalFieldHandled();
    } else if (externalActiveField) {
      onExternalFieldHandled && onExternalFieldHandled();
    }
  }, [externalActiveField]);

  useEffect(() => {
    const valid = ["role", "loc", "salary", "exp"];
    if (autoFocusField && valid.includes(autoFocusField)) {
      setActiveField(autoFocusField);
    }
  }, [autoFocusField]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (skipCloseRef.current) { skipCloseRef.current = false; return; }
      if (barRef.current && !barRef.current.contains(e.target)) setActiveField(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  useEffect(() => {
    const h = e => { if (e.key === "Escape") { setActiveField(null); onCollapse && onCollapse(); } };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onCollapse]);

  const handleFieldMouseDown = useCallback((fieldName, e) => {
    e.stopPropagation();
    skipCloseRef.current = true;
    setActiveField(fieldName);
  }, []);

  const advanceToNext = useCallback((currentField) => {
    const idx  = FIELD_ORDER.indexOf(currentField);
    const next = FIELD_ORDER[idx + 1];
    setTimeout(() => setActiveField(next ?? null), 80);
  }, []);

  const handleSearch = () => {
    const p = new URLSearchParams();
    if (keyword.trim())  p.append("keyword",    keyword.trim());
    if (location.trim()) p.append("location",   location.trim());
    if (salary)          p.append("salary",     salary);
    if (experience)      p.append("experience", experience);
    navigate(`/employee/jobs?${p.toString()}`);
    setActiveField(null);
    onCollapse && onCollapse();
  };

  const filteredRoles = roleQuery.trim()
    ? POPULAR_ROLES.filter(r => r.label.toLowerCase().includes(roleQuery.toLowerCase()))
    : POPULAR_ROLES;

  return (
    <div ref={barRef} className="nsb-wrap">
      <div className={`nsb-bar ${activeField ? "nsb-bar--active" : ""}`}>
        <div className="nsb-slider-bg" style={sliderStyle} aria-hidden="true" />

        <div
          ref={el => { fieldRefs.current["role"] = el; }}
          className={`nsb-field nsb-field--role ${activeField === "role" ? "nsb-field--open" : ""}`}
          onMouseDown={(e) => handleFieldMouseDown("role", e)}
        >
          <span className="nsb-field__label">Role</span>
          <span className={`nsb-field__val ${!keyword ? "nsb-field__val--ph" : ""}`}>
            {keyword || "Job title or skill"}
          </span>
          {keyword && activeField === "role" && (
            <button className="nsb-field__clear"
              onMouseDown={e => { e.stopPropagation(); setKeyword(""); setRoleQuery(""); }}>×</button>
          )}
        </div>

        <div className={`nsb-divider ${activeField ? "nsb-divider--hide" : ""}`} />

        <div
          ref={el => { fieldRefs.current["loc"] = el; }}
          className={`nsb-field nsb-field--loc ${activeField === "loc" ? "nsb-field--open" : ""}`}
          onMouseDown={(e) => handleFieldMouseDown("loc", e)}
        >
          <span className="nsb-field__label">Location</span>
          <span className={`nsb-field__val ${!location ? "nsb-field__val--ph" : ""}`}>
            {location || "City or Remote"}
          </span>
          {location && activeField === "loc" && (
            <button className="nsb-field__clear"
              onMouseDown={e => { e.stopPropagation(); setLocation(""); }}>×</button>
          )}
        </div>

        <div className={`nsb-divider ${activeField ? "nsb-divider--hide" : ""}`} />

        <div
          ref={el => { fieldRefs.current["salary"] = el; }}
          className={`nsb-field nsb-field--salary ${activeField === "salary" ? "nsb-field--open" : ""}`}
          onMouseDown={(e) => handleFieldMouseDown("salary", e)}
        >
          <span className="nsb-field__label">Salary</span>
          <span className={`nsb-field__val ${!salary ? "nsb-field__val--ph" : ""}`}>
            {salary || "Any range"}
          </span>
          {salary && activeField === "salary" && (
            <button className="nsb-field__clear"
              onMouseDown={e => { e.stopPropagation(); setSalary(""); }}>×</button>
          )}
        </div>

        <div className={`nsb-divider ${activeField ? "nsb-divider--hide" : ""}`} />

        <div
          ref={el => { fieldRefs.current["exp"] = el; }}
          className={`nsb-field nsb-field--exp ${activeField === "exp" ? "nsb-field--open" : ""}`}
          onMouseDown={(e) => handleFieldMouseDown("exp", e)}
        >
          <span className="nsb-field__label">Experience</span>
          <span className={`nsb-field__val ${!experience ? "nsb-field__val--ph" : ""}`}>
            {experience || "Any level"}
          </span>
          {experience && activeField === "exp" && (
            <button className="nsb-field__clear"
              onMouseDown={e => { e.stopPropagation(); setExperience(""); }}>×</button>
          )}
        </div>

        <button
          className={`nsb-btn ${activeField ? "nsb-btn--expanded" : ""}`}
          onClick={handleSearch}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          {activeField && <span className="nsb-btn__text">Search</span>}
        </button>
      </div>

      {activeField === "role" && (
        <div className="nsb-dropdown nsb-dropdown--role">
          <div className="nsb-dd-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="#717171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              className="nsb-dd-search__input"
              type="text"
              placeholder="Search job titles, skills…"
              value={roleQuery}
              autoFocus
              onChange={e => { setRoleQuery(e.target.value); setKeyword(e.target.value); }}
              onKeyDown={e => { if (e.key === "Enter") { setActiveField(null); handleSearch(); } }}
            />
            {roleQuery && (
              <button className="nsb-dd-search__clear"
                onMouseDown={e => { e.stopPropagation(); setRoleQuery(""); setKeyword(""); }}>×</button>
            )}
          </div>
          <div className="nsb-dd-section-label">
            {roleQuery ? `${filteredRoles.length} matching roles` : "Popular job roles"}
          </div>
          <div className="nsb-dd-list">
            {filteredRoles.map((r, i) => (
              <button key={i}
                className={`nsb-dd-item ${keyword === r.label ? "nsb-dd-item--active" : ""}`}
                onMouseDown={() => { setKeyword(r.label); setRoleQuery(""); advanceToNext("role"); }}>
                <div className="nsb-dd-item__icon-wrap"><span>{r.icon}</span></div>
                <div className="nsb-dd-item__copy">
                  <span className="nsb-dd-item__label">{r.label}</span>
                  <span className="nsb-dd-item__sub">{r.sub}</span>
                </div>
                {keyword === r.label && <span className="nsb-dd-item__check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeField === "loc" && (
        <div className="nsb-dropdown nsb-dropdown--loc">
          <div className="nsb-dd-section-label">Suggested locations</div>
          <div className="nsb-dd-list">
            {POPULAR_CITIES.map((c, i) => (
              <button key={i}
                className={`nsb-dd-item ${location === c.label ? "nsb-dd-item--active" : ""}`}
                onMouseDown={() => { setLocation(c.label); advanceToNext("loc"); }}>
                <div className="nsb-dd-item__icon-wrap nsb-dd-item__icon-wrap--city"><span>{c.icon}</span></div>
                <div className="nsb-dd-item__copy">
                  <span className="nsb-dd-item__label">{c.label}</span>
                  <span className="nsb-dd-item__sub">{c.sub}</span>
                </div>
                {location === c.label && <span className="nsb-dd-item__check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeField === "salary" && (
        <div className="nsb-dropdown nsb-dropdown--salary">
          <div className="nsb-dd-section-label">Select salary range</div>
          <div className="nsb-dd-list">
            {SALARY_OPTIONS.map((s, i) => (
              <button key={i}
                className={`nsb-dd-item nsb-dd-item--wide ${salary === s.val ? "nsb-dd-item--active" : ""}`}
                onMouseDown={() => { setSalary(s.val); advanceToNext("salary"); }}>
                <div className="nsb-dd-item__icon-wrap"><span>{s.icon}</span></div>
                <div className="nsb-dd-item__copy">
                  <span className="nsb-dd-item__label">{s.label}</span>
                  <span className="nsb-dd-item__sub">{s.sub}</span>
                </div>
                {salary === s.val && <span className="nsb-dd-item__check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeField === "exp" && (
        <div className="nsb-dropdown nsb-dropdown--exp">
          <div className="nsb-dd-section-label">Select experience level</div>
          <div className="nsb-dd-list">
            {EXPERIENCE_OPTIONS.map((ex, i) => (
              <button key={i}
                className={`nsb-dd-item nsb-dd-item--wide ${experience === ex.val ? "nsb-dd-item--active" : ""}`}
                onMouseDown={() => { setExperience(ex.val); advanceToNext("exp"); }}>
                <div className="nsb-dd-item__icon-wrap"><span>{ex.icon}</span></div>
                <div className="nsb-dd-item__copy">
                  <span className="nsb-dd-item__label">{ex.label}</span>
                  <span className="nsb-dd-item__sub">{ex.sub}</span>
                </div>
                {experience === ex.val && <span className="nsb-dd-item__check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeField && (
        <div className="nsb-backdrop" onClick={() => setActiveField(null)} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MINI SEARCH PILL
══════════════════════════════════════════════════════════ */
function MiniSearchPill({ onClick, values = {} }) {
  const SEGS = [
    { id: "role",   label: "Role",       field: "role"   },
    { id: "loc",    label: "Location",   field: "loc"    },
    { id: "salary", label: "Salary",     field: "salary" },
    { id: "exp",    label: "Experience", field: "exp"    },
  ];

  return (
    <div className="nav-mini-pill" aria-label="Open search">
      <span className="nav-mini-pill__segments">
        {SEGS.map((seg, i) => (
          <React.Fragment key={seg.id}>
            <span
              className="nav-mini-pill__seg-wrap"
              onClick={(e) => { e.stopPropagation(); onClick(seg.field); }}
            >
              <span className="nav-mini-pill__seg-label">{seg.label}</span>
            </span>
            {i < SEGS.length - 1 && <span className="nav-mini-pill__div" />}
          </React.Fragment>
        ))}
      </span>
      <span
        className="nav-mini-pill__icon"
        onClick={(e) => { e.stopPropagation(); onClick("role"); }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   SEARCH OVERLAY
══════════════════════════════════════════════════════════ */
function SearchOverlay({ onCollapse, openField, currentUser, isEmployer, onSwitcher, onLoginOpen, onPhotoUpload, onLogout }) {
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return createPortal(
    <>
      <div className="nsb-overlay-backdrop" onClick={onCollapse} />
      <div className="nsb-overlay-bar">
        <div className="nsb-overlay-nav-header">
          <Link to="/" className="nav-logo nsb-overlay-logo" onClick={onCollapse}>
            <img src={Logo} alt="TalentHive" />
          </Link>
          <div className="nsb-overlay-center">
            <NavToolTabs location={location} onTabClick={() => {}} />
          </div>
          <div className="nav-right">
            <NavSwitcher isEmployer={isEmployer} onClick={onSwitcher} />
            {currentUser ? (
              <UserAvatar user={currentUser} onLogout={onLogout} onPhotoUpload={onPhotoUpload} />
            ) : (
              <button className="nav-signin-btn"
                onClick={() => { onCollapse(); onLoginOpen(); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="7" r="4"/>
                  <path d="M5.5 21a6.5 6.5 0 0 1 13 0"/>
                </svg>
                <span className="nav-signin-btn__text">Sign In</span>
              </button>
            )}
            <button
              className="nav-burger"
              style={{ display: "flex" }}
              onClick={onCollapse}
              aria-label="Close search"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="nsb-overlay-searchbar">
          <SearchBarCore
            autoFocusField={openField}
            onCollapse={onCollapse}
          />
        </div>
      </div>
    </>,
    document.body
  );
}

/* ══════════════════════════════════════
   TOOL TABS
══════════════════════════════════════ */
function NavToolTabs({ location: loc, onTabClick }) {
  const navigate    = useNavigate();
  const [calcOpen,   setCalcOpen]   = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);
  const calcBtnRef  = useRef(null);
  const dropRef     = useRef(null);
  const insideRef   = useRef(false);

  const activeTabId   = TABS.find(t => t.path && loc.pathname.startsWith(t.path))?.id || null;
  const isOnCalcPage  = CALCULATOR_TOOLS.some(t => loc.pathname === t.path);
  const calcIsActive  = isOnCalcPage || calcOpen || hoveredTab === "Calculator";

  useEffect(() => { setCalcOpen(false); }, [loc.pathname]);

  useEffect(() => {
    if (!calcOpen) return;
    const onPointerMove = (e) => {
      const overBtn  = calcBtnRef.current?.contains(e.target) || calcBtnRef.current === e.target;
      const overDrop = dropRef.current?.contains(e.target)    || dropRef.current === e.target;
      if (!overBtn && !overDrop) {
        insideRef.current = false;
        setCalcOpen(false);
        setHoveredTab(null);
      }
    };
    document.addEventListener("pointermove", onPointerMove);
    return () => document.removeEventListener("pointermove", onPointerMove);
  }, [calcOpen]);

  return (
    <>
      <div className="nav-tool-tabs">
        {TABS.map(tab => {
          const isCalc    = tab.id === "Calculator";
          const isActive  = isCalc ? calcIsActive : activeTabId === tab.id;
          const gifActive = hoveredTab === tab.id || isActive;
          const iconSrc   = gifActive ? tab.gif : tab.png;

          return (
            <button
              key={tab.id}
              ref={isCalc ? calcBtnRef : undefined}
              className={`nav-tool-tab ${isActive ? "nav-tool-tab--active" : ""}`}
              style={{
                background:  gifActive ? "#ffffff" : "transparent",
                boxShadow:   gifActive ? "0 2px 12px rgba(0,0,0,0.10)" : "none",
                border:      gifActive ? "1.5px solid #e4e4e4" : "1.5px solid transparent",
                transition:  "background 0.15s, box-shadow 0.15s, border-color 0.15s",
              }}
              onMouseEnter={() => {
                setHoveredTab(tab.id);
                if (isCalc) { insideRef.current = true; setCalcOpen(true); }
              }}
              onMouseLeave={() => {
                setHoveredTab(null);
              }}
              onClick={() => {
                if (isCalc) {
                  setCalcOpen(o => !o);
                } else {
                  setCalcOpen(false);
                  navigate(tab.path);
                  onTabClick(tab.field);
                }
              }}
              aria-haspopup={isCalc ? "menu" : undefined}
              aria-expanded={isCalc ? calcOpen : undefined}
            >
              <img src={iconSrc} alt={tab.label} className="nav-tool-tab__gif" />
              <span className="nav-tool-tab__label">{tab.label}</span>
              {isActive && <span className="nav-tool-tab__bar" />}
            </button>
          );
        })}
      </div>

      <CalcDropdown
        anchorRef={calcBtnRef}
        dropRef={dropRef}
        open={calcOpen}
        onClose={() => { setCalcOpen(false); setHoveredTab(null); }}
        onMouseEnter={() => { insideRef.current = true; setHoveredTab("Calculator"); }}
        onMouseLeave={() => {}}
      />
    </>
  );
}

/* ── ScrollToggleMenu ── */
function ScrollToggleMenu({ open, onClose, buttonRef, style }) {
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!open) return;
    const h1 = e => {
      if (panelRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      onClose();
    };
    const h2 = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", h1);
    document.addEventListener("touchstart", h1);
    document.addEventListener("keydown", h2);
    return () => {
      document.removeEventListener("mousedown", h1);
      document.removeEventListener("touchstart", h1);
      document.removeEventListener("keydown", h2);
    };
  }, [buttonRef, onClose, open]);

  if (!open) return null;
  return createPortal(
    <div className="nav-toggle-panel" ref={panelRef} style={style} role="menu">
      <div className="nav-toggle-panel__section-label">Tools</div>
      <div className="nav-toggle-panel__grid">
        {TOOLS.map(item => (
          <button key={item.id} type="button"
            className={`nav-toggle-panel__item ${location.pathname === item.path ? "nav-toggle-panel__item--active" : ""}`}
            onClick={() => { onClose(); navigate(item.path); }}>
            <span className="nav-toggle-panel__icon">{item.icon}</span>
            <span className="nav-toggle-panel__copy">
              <span className="nav-toggle-panel__label">{item.label}</span>
              <span className="nav-toggle-panel__desc">{item.desc}</span>
            </span>
            <span className="nav-toggle-panel__arrow">›</span>
          </button>
        ))}
      </div>
      <div className="nav-toggle-panel__divider" />
      <div className="nav-toggle-panel__section-label">Calculators</div>
      <div className="nav-toggle-panel__grid">
        {CALCULATOR_TOOLS.map(item => (
          <button key={item.id} type="button"
            className={`nav-toggle-panel__item ${location.pathname === item.path ? "nav-toggle-panel__item--active" : ""}`}
            onClick={() => { onClose(); navigate(item.path); }}>
            <span className="nav-toggle-panel__icon">{item.icon}</span>
            <span className="nav-toggle-panel__copy">
              <span className="nav-toggle-panel__label">{item.label}</span>
              <span className="nav-toggle-panel__desc">{item.desc}</span>
            </span>
            <span className="nav-toggle-panel__arrow">›</span>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}

/* ── NavSwitcher ── */
function NavSwitcher({ isEmployer, onClick }) {
  return (
    <div className="nav-switcher" onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick()}
      aria-label={`Switch to ${isEmployer ? "Candidate" : "Company"} view`}>
      <span className={`nav-switcher__pill ${isEmployer ? "nav-switcher__pill--right" : "nav-switcher__pill--left"}`} />
      <span className={`nav-switcher__label ${!isEmployer ? "nav-switcher__label--on" : ""}`}>Candidate</span>
      <span className={`nav-switcher__label ${isEmployer ? "nav-switcher__label--on" : ""}`}>Company</span>
    </div>
  );
}

/* ── UserAvatar ── */
function UserAvatar({ user, onLogout, onPhotoUpload }) {
  const [open,     setOpen]     = useState(false);
  const [imgError, setImgError] = useState(false);
  const ref     = useRef(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const photo       = getProfilePhoto(user);
  const name        = getDisplayName(user);
  const initial     = (name || "U")[0].toUpperCase();
  const showPhoto   = photo && !imgError;
  const accent      = avatarColor(name);
  const profilePath = getProfilePath(user);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleFileChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOpen(false);
    await onPhotoUpload(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="nav-user-wrap" ref={ref}>
      <button className="nav-user-avatar" onClick={() => setOpen(o => !o)} aria-label={`Account — ${name}`}>
        {showPhoto
          ? <img src={photo} alt={name} className="nav-user-avatar__img" onError={() => setImgError(true)} />
          : <span className="nav-user-avatar__initial" style={{ background: accent }}>{initial}</span>
        }
        <span className="nav-user-avatar__dot" />
      </button>
      {open && (
        <div className="nav-user-drop">
          <div className="nav-user-drop__head">
            <div className="nav-user-drop__avatar-lg" style={{ background: accent }}>
              {showPhoto ? <img src={photo} alt={name} onError={() => setImgError(true)} /> : <span>{initial}</span>}
            </div>
            <div className="nav-user-drop__info">
              <div className="nav-user-drop__name">{name}</div>
              <div className="nav-user-drop__email">{user.email}</div>
            </div>
          </div>
          <div className="nav-user-drop__divider" />
          <button className="nav-user-drop__item"
            onClick={() => { setOpen(false); navigate(profilePath); }}><span>👤</span> View Profile</button>
          <button className="nav-user-drop__item"
            onClick={() => fileRef.current?.click()}>
            <span>📷</span> {photo && !imgError ? "Change Photo" : "Upload Photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: "none" }} onChange={handleFileChange} />
          <div className="nav-user-drop__divider" />
          <button className="nav-user-drop__item nav-user-drop__item--danger"
            onClick={() => { setOpen(false); onLogout(); }}><span>🚪</span> Sign Out</button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN NAV
══════════════════════════════════════════════════════════ */
export default function Nav() {
  const [openLogin,    setOpenLogin]    = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [toggleOpen,   setToggleOpen]   = useState(false);
  const [toggleStyle,  setToggleStyle]  = useState(null);
  const [currentUser,  setCurrentUser]  = useState(() => readStoredUser());
  const [photoToast,   setPhotoToast]   = useState("");
  const [uploading,    setUploading]    = useState(false);
  const [isScrolled,   setIsScrolled]   = useState(false);
  const [openField,    setOpenField]    = useState(null);
  const [overlayOpen,  setOverlayOpen]  = useState(false);
  const [overlayField, setOverlayField] = useState(null);
  const [pillValues,   setPillValues]   = useState({});

  const navigate     = useNavigate();
  const location     = useLocation();
  const toggleBtnRef = useRef(null);
  const isEmployer   = location.pathname.includes("employer");

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 60);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setCurrentUser(readStoredUser()); }, [location.pathname]);
  useEffect(() => {
    const fn = e => {
      if (["th_user","user","th_token","token"].includes(e.key)) setCurrentUser(readStoredUser());
    };
    window.addEventListener("storage", fn);
    return () => window.removeEventListener("storage", fn);
  }, []);
  useEffect(() => {
    const fn = () => setCurrentUser(readStoredUser());
    window.addEventListener("auth:update", fn);
    return () => window.removeEventListener("auth:update", fn);
  }, []);

  const positionTogglePanel = useCallback(() => {
    const width = Math.min(380, Math.max(260, window.innerWidth - 24));
    if (!toggleBtnRef.current) {
      setToggleStyle({ top: 80, left: Math.max(12, window.innerWidth - width - 12), width });
      return;
    }
    const rect = toggleBtnRef.current.getBoundingClientRect();
    const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
    setToggleStyle({ top: Math.round(rect.bottom + 10), left: Math.round(left), width });
  }, []);

  useEffect(() => {
    if (!toggleOpen) return;
    positionTogglePanel();
    window.addEventListener("resize", positionTogglePanel);
    window.addEventListener("scroll", positionTogglePanel, true);
    return () => {
      window.removeEventListener("resize", positionTogglePanel);
      window.removeEventListener("scroll", positionTogglePanel, true);
    };
  }, [toggleOpen, positionTogglePanel]);

  useEffect(() => { setToggleOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen || openLogin ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen, openLogin]);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const id = "th-nav-kf";
    if (document.getElementById(id)) return;
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      @keyframes navDropIn    { from{opacity:0;transform:translateY(-8px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
      @keyframes nsbDdIn      { from{opacity:0;transform:translateY(8px) scale(.98)}  to{opacity:1;transform:translateY(0) scale(1)} }
      @keyframes spin         { to{transform:rotate(360deg)} }
      @keyframes overlaySlide { from{opacity:0;transform:translateY(-22px)} to{opacity:1;transform:translateY(0)} }
      @keyframes backdropFade { from{opacity:0} to{opacity:1} }
      @keyframes miniPillIn   { from{opacity:0;transform:scale(.9)} to{opacity:1;transform:scale(1)} }
    `;
    document.head.appendChild(s);
  }, []);

  const handleLoginSuccess = useCallback(({ token, user }) => {
    saveUser(user, token);
    setCurrentUser(user);
    setOpenLogin(false);
    window.dispatchEvent(new Event("auth:update"));
    navigate(getProfilePath(user));
  }, [navigate]);

  const handleLogout = useCallback(() => {
    clearUser();
    setCurrentUser(null);
    window.dispatchEvent(new Event("auth:update"));
    navigate("/");
  }, [navigate]);

  const handlePhotoUpload = useCallback(async file => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setPhotoToast("❌ Photo must be under 5 MB");
      setTimeout(() => setPhotoToast(""), 3500);
      return;
    }
    setUploading(true);
    try {
      const token = readStoredToken();
      const fd = new FormData();
      fd.append("photo", file);
      const res = await fetch(`${API_BASE}/candidate/upload-photo`, {
        method: "POST", credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Upload failed");
      const updated = { ...readStoredUser(), profile_photo: data.photo_url || data.url };
      saveUser(updated, token);
      setCurrentUser(updated);
      window.dispatchEvent(new Event("auth:update"));
      setPhotoToast("✅ Profile photo updated!");
      setTimeout(() => setPhotoToast(""), 3500);
    } catch (err) {
      setPhotoToast("❌ " + (err.message || "Upload failed"));
      setTimeout(() => setPhotoToast(""), 3500);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleSwitcher  = useCallback(() => { navigate(isEmployer ? "/employee" : "/employer"); setMenuOpen(false); }, [isEmployer, navigate]);
  const handleDrawerNav = useCallback(path => { navigate(path); setMenuOpen(false); }, [navigate]);
  const handleTabClick  = useCallback(() => {}, []);

  const handleMiniPillClick = useCallback((field = "role") => {
    setOverlayField(field);
    setOverlayOpen(true);
  }, []);

  const handleOverlayCollapse = useCallback(() => {
    setOverlayOpen(false);
    setOverlayField(null);
  }, []);

  const profilePath = getProfilePath(currentUser);
  const displayName = getDisplayName(currentUser);
  const isHeroPage  = location.pathname === "/";

  return (
    <>
      <div
        className="nav-spacer"
        style={{
          height: isHeroPage ? "0px" : isScrolled ? "0px" : "0px",
          transition: "height 0.45s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      />

      {(photoToast || uploading) && (
        <div style={{
          position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)",
          background: uploading ? "#263012" : photoToast.startsWith("❌") ? "#b91c1c" : "#16a34a",
          color:"#fff", padding:"12px 22px", borderRadius:12, fontWeight:700, fontSize:13.5,
          zIndex:99999, boxShadow:"0 8px 28px rgba(0,0,0,.2)",
          display:"flex", alignItems:"center", gap:10, animation:"navDropIn .25s ease", whiteSpace:"nowrap",
        }}>
          {uploading
            ? <><span style={{
                width:16, height:16,
                border:"2px solid rgba(255,255,255,.4)", borderTopColor:"#fff",
                borderRadius:"50%", animation:"spin .7s linear infinite", display:"inline-block"
              }} />Uploading…</>
            : photoToast}
        </div>
      )}

      <nav className={`nav-root nav-root--v2 ${isScrolled ? "nav-root--scrolled nav-root--collapsed" : "nav-root--expanded"}`}>
        <div className="nav-bar nav-bar--main">
          <Link to="/" className="nav-logo" onClick={() => setMenuOpen(false)}>
            <img src={Logo} alt="TalentHive" />
          </Link>

          <div className="nav-center">
            {isScrolled
              ? <MiniSearchPill onClick={handleMiniPillClick} values={pillValues} />
              : <NavToolTabs location={location} onTabClick={handleTabClick} />
            }
          </div>

          <div className="nav-right">
            <NavSwitcher isEmployer={isEmployer} onClick={handleSwitcher} />

            <button ref={toggleBtnRef} type="button"
              className={`nav-scroll-toggle ${toggleOpen ? "nav-scroll-toggle--open" : ""}`}
              onClick={() => { setToggleOpen(o => !o); requestAnimationFrame(positionTogglePanel); }}
              aria-haspopup="menu" aria-expanded={toggleOpen} aria-label="Open tools menu">
              <span className="nav-scroll-toggle__bar" />
              <span className="nav-scroll-toggle__bar" />
              <span className="nav-scroll-toggle__bar" />
            </button>

            {currentUser ? (
              <UserAvatar user={currentUser} onLogout={handleLogout} onPhotoUpload={handlePhotoUpload} />
            ) : (
              <button className="nav-signin-btn"
                onClick={() => { setMenuOpen(false); setOpenLogin(true); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/>
                </svg>
                <span className="nav-signin-btn__text">Sign In</span>
              </button>
            )}

            <button className="nav-burger"
              onClick={() => setMenuOpen(p => !p)}
              aria-label={menuOpen ? "Close" : "Open menu"} aria-expanded={menuOpen}>
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>

        <div
          className={`nav-searchrow ${isScrolled ? "nav-searchrow--hidden" : "nav-searchrow--visible"}`}
          aria-hidden={isScrolled}
        >
          <SearchBarCore
            externalActiveField={openField}
            onExternalFieldHandled={() => setOpenField(null)}
          />
        </div>
      </nav>

      {overlayOpen && (
        <SearchOverlay
          onCollapse={handleOverlayCollapse}
          openField={overlayField}
          currentUser={currentUser}
          isEmployer={isEmployer}
          onSwitcher={handleSwitcher}
          onLoginOpen={() => setOpenLogin(true)}
          onPhotoUpload={handlePhotoUpload}
          onLogout={handleLogout}
        />
      )}

      <ScrollToggleMenu
        open={toggleOpen}
        onClose={() => setToggleOpen(false)}
        buttonRef={toggleBtnRef}
        style={toggleStyle}
      />

      {menuOpen && (
        <div className="nav-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}

      <aside className={`nav-drawer ${menuOpen ? "nav-drawer--open" : ""}`} aria-hidden={!menuOpen}>
        <div className="nav-drawer__header">
          <img src={Logo} alt="TalentHive" className="nav-drawer__logo" />
          <button className="nav-drawer__close-btn" onClick={() => setMenuOpen(false)}>✕</button>
        </div>

        {currentUser && (
          <div className="nav-drawer__user-row">
            <button className="nav-drawer__user-main"
              onClick={() => { setMenuOpen(false); navigate(profilePath); }}>
              <div className="nav-drawer__user-avatar" style={{ background: avatarColor(displayName) }}>
                {getProfilePhoto(currentUser)
                  ? <img src={getProfilePhoto(currentUser)} alt={displayName}
                      onError={e => { e.currentTarget.style.display = "none"; }} />
                  : <span>{displayName[0].toUpperCase()}</span>
                }
              </div>
              <div className="nav-drawer__user-info">
                <div className="nav-drawer__user-name">{displayName}</div>
                <div className="nav-drawer__user-sub">
                  {currentUser.role === "admin" ? "Admin Panel →"
                    : currentUser.role === "employer" ? "Recruiter Dashboard →"
                    : "View my profile →"}
                </div>
              </div>
            </button>
            <label className="nav-drawer__photo-btn" title="Change photo">
              📷
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => { setMenuOpen(false); handlePhotoUpload(e.target.files?.[0]); }} />
            </label>
          </div>
        )}

        <div className="nav-drawer__switcher-wrap">
          <NavSwitcher isEmployer={isEmployer} onClick={handleSwitcher} />
        </div>

        <span className="nav-drawer__section-label">Tools</span>
        <div className="nav-drawer__tool-list">
          {TOOLS.map(t => (
            <button key={t.id}
              className={`nav-drawer__tool-btn ${location.pathname === t.path ? "nav-drawer__tool-btn--active" : ""}`}
              onClick={() => handleDrawerNav(t.path)}>
              <span className="nav-drawer__tool-icon">{t.icon}</span>
              <span className="nav-drawer__tool-name">{t.label}</span>
              <span className="nav-drawer__tool-arrow">›</span>
            </button>
          ))}
        </div>

        <span className="nav-drawer__section-label">Calculators</span>
        <div className="nav-drawer__tool-list">
          {CALCULATOR_TOOLS.map(t => (
            <button key={t.id}
              className={`nav-drawer__tool-btn ${location.pathname === t.path ? "nav-drawer__tool-btn--active" : ""}`}
              onClick={() => handleDrawerNav(t.path)}>
              <span className="nav-drawer__tool-icon">{t.icon}</span>
              <span className="nav-drawer__tool-name">{t.label}</span>
              <span className="nav-drawer__tool-arrow">›</span>
            </button>
          ))}
        </div>

        <div className="nav-drawer__footer">
          {currentUser ? (
            <button
              className="nav-drawer__footer-signin nav-drawer__footer-signout"
              onClick={() => { setMenuOpen(false); handleLogout(); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign Out
            </button>
          ) : (
            <button className="nav-drawer__footer-signin"
              onClick={() => { setMenuOpen(false); setOpenLogin(true); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/>
              </svg>
              Sign In
            </button>
          )}
        </div>
      </aside>

      <Login isOpen={openLogin} onClose={() => setOpenLogin(false)} onLoginSuccess={handleLoginSuccess} />
    </>
  );
}